import React, { useState } from "react";
import Navbar from "../components/Navbar";
import { 
  FileText, Users, Clock, Eye, TrendingUp, Plus, BarChart3, 
  PieChart as PieChartIcon, AlertTriangle, CheckCircle2, XCircle, 
  Copy, ExternalLink, Search, Sparkles, Send, Bot, Star, Calendar 
} from "lucide-react";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, Legend, AreaChart, Area, LineChart, Line 
} from "recharts";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { useLang } from "../hooks/useLang";
import { useAuthContext } from "../hooks/useAuthContext";
import { motion, AnimatePresence } from "framer-motion";


const generateAIResults = (criteria, respondents) => {
  const completed = respondents.filter(r => r.completed);
  if (!criteria.trim() || completed.length === 0) return null;
  
  const criteriaLower = criteria.toLowerCase();
  
  const scored = completed.map(r => {
    let matchScore = 50 + Math.random() * 20; 
    if (r.skills.some(s => criteriaLower.includes(s.toLowerCase()))) matchScore += 25;
    if (criteriaLower.includes("experience") && r.experience !== "None") matchScore += 10;
    if (r.score > 80) matchScore += 10;
    return { ...r, aiScore: Math.min(99, Math.round(matchScore)) };
  });
  
  scored.sort((a, b) => b.aiScore - a.aiScore);
  const reasoning = `Based on your criteria "${criteria}", I analyzed ${completed.length} completed applicants. I evaluated their skills, experience level, response quality (score), and time investment. Here are the best matches:`;
  return { candidates: scored, reasoning };
};

const Forms = () => {
  const { theme } = useTheme();
  const { urlLanguage } = useLang();
  const { user, token } = useAuthContext();
  const navigate = useNavigate();
  const isDark = theme === "dark";
  
  const [realForms, setRealForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  React.useEffect(() => {
    const fetchForms = async () => {
      try {
        const res = await fetch('/api/forms', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok && data.forms) {
          const mapped = data.forms.map(f => ({
            ...f,
            createdAt: new Date(f.createdAt).toLocaleDateString(),
            closedAt: null,
            responses: f.responses || 0,
            views: f.views || 0,
            avgTime: f.avgTime || "0m 0s",
            completionRate: f.completionRate || 0,
            url: `${window.location.origin}/v/${f.urlSlug}`,
            questions: f.questions || [],
            respondents: [],
            dailyResponses: [{day:"Mon",count:0}],
            timeline: []
          }));
          setRealForms(mapped);
          if (mapped.length > 0 && !selectedForm) setSelectedForm(mapped[0]);
          else if (selectedForm) {
            // Update the selected form if it was already selected but data refreshed
            const updated = mapped.find(f => f.id === selectedForm.id);
            if (updated) setSelectedForm(updated);
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    if (user) fetchForms();
  }, [user]);

  const displayForms = realForms;


  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResults, setAiResults] = useState(null);

  const [toastMsg, setToastMsg] = useState(null);

  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3000);
  };

  const completedCount = selectedForm?.respondents?.filter(r => r.completed).length || 0;
  const droppedCount = selectedForm?.respondents?.filter(r => !r.completed).length || 0;
  const pieData = [
    { name: "Completed", value: completedCount },
    { name: "Dropped Off", value: droppedCount },
  ];
  const pieColors = isDark ? ["#10b981", "#ef4444"] : ["#059669", "#dc2626"];
  const barColor = isDark ? "#3b82f6" : "#2563eb";
  const areaColor = isDark ? "#8b5cf6" : "#6d28d9";
  const lineColor2 = isDark ? "#f59e0b" : "#d97706";

  const tooltipStyle = {
    backgroundColor: isDark ? "#1e293b" : "#ffffff",
    border: "1px solid",
    borderColor: isDark ? "#334155" : "#e2e8f0",
    borderRadius: "12px",
    fontSize: "12px",
    color: isDark ? "#f8fafc" : "#0f172a",
  };

  const tickFill = isDark ? "#94a3b8" : "#64748b";
  const gridStroke = isDark ? "#334155" : "#e2e8f0";

  const questionTimeData = selectedForm?.questions?.map(q => ({
    name: q.text.length > 20 ? q.text.substring(0, 20) + "…" : q.text,
    avgTime: q.avgTime,
    dropoff: q.dropoff,
  })) || [];

  const copyLink = () => {
    navigator.clipboard.writeText(selectedForm.url);
    showToast("Link copied to clipboard!");
  };

  const handleAISearch = () => {
    if (!aiPrompt.trim()) return;
    setAiLoading(true);
    setAiResults(null);
    setTimeout(() => {
      const results = generateAIResults(aiPrompt, selectedForm.respondents);
      setAiResults(results);
      setAiLoading(false);
    }, 1500);
  };

  const filteredForms = displayForms.filter(f => f.title.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Custom Toast */}
      <AnimatePresence>
        {toastMsg && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-20 left-1/2 -translate-x-1/2 z-[200] bg-foreground text-background px-4 py-2 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2"
          >
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            {toastMsg}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-[1440px] mx-auto px-6 py-6 font-sans">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-[28px] font-display font-bold text-foreground">My Forms</h1>
            <p className="text-xs text-muted-foreground mt-1">Create forms, track responses & analyze candidates</p>
          </div>
          <button
            onClick={() => navigate(urlLanguage("/forms/create"))}
            className="h-10 px-5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-all flex items-center gap-2 shadow-lg shadow-primary/20 active:scale-95"
          >
            <Plus className="w-4 h-4" /> Create Form
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Sidebar Forms List */}
          <div className="col-span-1 lg:col-span-3 space-y-3">
            <div className={`flex items-center gap-3 px-4 h-11 rounded-2xl border transition-all ${isDark ? "bg-card/30 border-border/40 focus-within:border-primary/50" : "bg-muted/50 border-border focus-within:border-primary/50"}`}>
              <Search className="w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search forms..."
                className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
              />
            </div>
            
            <div className="space-y-3 max-h-[calc(100vh-280px)] overflow-y-auto scrollbar-hide pr-1">
              {filteredForms.map((form) => (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  key={form.id}
                  onClick={() => { setSelectedForm(form); setActiveTab("overview"); setAiResults(null); setAiPrompt(""); }}
                  className={`w-full text-left p-4 rounded-xl border border-l-4 transition-all duration-300 ${
                    selectedForm?.id === form.id
                      ? "bg-primary/5 border-primary shadow-[0_0_20px_-5px_rgba(var(--primary),0.1)]"
                      : "bg-card border-border border-l-transparent hover:border-primary/30 hover:bg-muted/50"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-xs font-black flex-shrink-0 transition-all ${
                        selectedForm?.id === form.id ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20" : "bg-muted text-muted-foreground border border-border/50"
                      }`}>
                        {form.title.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-bold truncate ${selectedForm.id === form.id ? "text-primary" : "text-foreground"}`}>{form.title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{form.createdAt}</p>
                      </div>
                    </div>
                    <span className={`text-[8px] px-2 py-0.5 rounded-full font-black uppercase tracking-[0.15em] flex-shrink-0 ${
                      form.status === "active" ? "bg-emerald-500/20 text-emerald-500 border border-emerald-500/20" : "bg-muted text-muted-foreground"
                    }`}>
                      {form.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-2.5 text-xs text-muted-foreground font-medium ml-11">
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {form.responses}</span>
                    <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {form.views}</span>
                  </div>
                </motion.button>
              ))}
            </div>
          </div>

          {/* Main Dashboard */}
          <div className="col-span-1 lg:col-span-9 space-y-6">
            {!selectedForm ? (
              <div className="bg-card rounded-2xl border border-border p-12 flex flex-col items-center justify-center text-center shadow-sm h-[600px]">
                <div className="w-20 h-20 rounded-3xl bg-primary/10 flex items-center justify-center mb-6">
                  <FileText className="w-10 h-10 text-primary" />
                </div>
                <h2 className="text-2xl font-display font-bold text-foreground">No form selected</h2>
                <p className="text-sm text-muted-foreground mt-2 max-w-sm">Select a form from the sidebar to view detailed analytics and manage responses.</p>
              </div>
            ) : (
              <>
                <div className="bg-card rounded-2xl border border-border p-5 sm:p-6 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-xl font-display font-bold text-foreground">{selectedForm.title}</h2>
                    <p className="text-sm text-muted-foreground flex items-center gap-2 mt-0.5">
                      <Calendar className="w-3.5 h-3.5" /> {selectedForm.createdAt} → {selectedForm.closedAt || "Present"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={copyLink} className="h-9 px-4 rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-muted hover:border-muted-foreground/30 transition-all flex items-center gap-2">
                    <Copy className="w-4 h-4" /> Copy Link
                  </button>
                  <button onClick={() => window.open(selectedForm.url, "_blank")} className="h-9 w-9 rounded-xl border border-border flex items-center justify-center text-foreground hover:bg-muted hover:border-muted-foreground/30 transition-all">
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: "Total Responses", value: selectedForm.responses, icon: Users, color: "text-blue-500", bg: "bg-blue-500/10" },
                { label: "Total Views", value: selectedForm.views, icon: Eye, color: "text-amber-500", bg: "bg-amber-500/10" },
                { label: "Avg. Time", value: selectedForm.avgTime, icon: Clock, color: "text-emerald-500", bg: "bg-emerald-500/10" },
                { label: "Completion Rate", value: `${selectedForm.completionRate}%`, icon: TrendingUp, color: "text-purple-500", bg: "bg-purple-500/10" },
              ].map((stat) => (
                <div key={stat.label} className="bg-card rounded-2xl border border-border p-4 shadow-sm hover:shadow-md transition-all">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-medium text-muted-foreground">{stat.label}</span>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${stat.bg}`}>
                      <stat.icon className={`w-4 h-4 ${stat.color}`} />
                    </div>
                  </div>
                  <p className="text-xl font-display font-bold text-foreground">{stat.value}</p>
                </div>
              ))}
            </div>

            <div className="flex bg-muted/50 p-1 rounded-xl overflow-x-auto no-scrollbar border border-border">
              {(["overview", "timeline", "respondents", "questions", "ai-finder"]).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 py-1.5 px-3 rounded-lg text-sm font-semibold capitalize transition-all whitespace-nowrap flex items-center justify-center gap-1.5 ${
                    activeTab === tab 
                      ? "bg-card text-foreground shadow-sm shadow-black/5" 
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  }`}
                >
                  {tab === "ai-finder" && <Sparkles className="w-4 h-4 text-purple-500" />}
                  {tab === "timeline" && <Calendar className="w-4 h-4" />}
                  {tab === "overview" && <BarChart3 className="w-4 h-4" />}
                  {tab === "ai-finder" ? "AI Finder" : tab}
                </button>
              ))}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {activeTab === "overview" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="bg-card rounded-2xl border border-border p-5 shadow-sm">
                      <h3 className="text-base font-bold text-foreground mb-6 flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-blue-500" /> Daily Responses
                      </h3>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={selectedForm.dailyResponses}>
                          <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                          <XAxis dataKey="day" tick={{ fontSize: 12, fill: tickFill }} axisLine={false} tickLine={false} dy={10} />
                          <YAxis tick={{ fontSize: 12, fill: tickFill }} axisLine={false} tickLine={false} dx={-10} />
                          <Tooltip cursor={{ fill: isDark ? '#334155' : '#f1f5f9' }} contentStyle={tooltipStyle} />
                          <Bar dataKey="count" fill={barColor} radius={[6, 6, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="bg-card rounded-2xl border border-border p-5 shadow-sm">
                      <h3 className="text-base font-bold text-foreground mb-6 flex items-center gap-2">
                        <PieChartIcon className="w-5 h-5 text-purple-500" /> Completion Rate
                      </h3>
                      <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                          <Pie data={pieData} cx="50%" cy="50%" innerRadius={65} outerRadius={90} paddingAngle={4} dataKey="value">
                            {pieData.map((_, i) => <Cell key={i} fill={pieColors[i]} />)}
                          </Pie>
                          <Tooltip contentStyle={tooltipStyle} />
                          <Legend 
                            verticalAlign="bottom" 
                            formatter={(value) => <span className="text-sm font-medium text-foreground ml-1">{value}</span>} 
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="bg-card rounded-2xl border border-border p-5 md:col-span-2 shadow-sm">
                      <h3 className="text-base font-bold text-foreground mb-6 flex items-center gap-2">
                        <Clock className="w-5 h-5 text-emerald-500" /> Interaction Metrics
                      </h3>
                      <ResponsiveContainer width="100%" height={240}>
                        <AreaChart data={questionTimeData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                          <defs>
                            <linearGradient id="colorArea" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={areaColor} stopOpacity={0.3}/>
                              <stop offset="95%" stopColor={areaColor} stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                          <XAxis dataKey="name" tick={{ fontSize: 11, fill: tickFill }} axisLine={false} tickLine={false} dy={10} />
                          <YAxis tick={{ fontSize: 12, fill: tickFill }} axisLine={false} tickLine={false} dx={-10} />
                          <Tooltip contentStyle={tooltipStyle} />
                          <Area type="monotone" dataKey="avgTime" stroke={areaColor} fillOpacity={1} fill="url(#colorArea)" strokeWidth={3} />
                          <Area type="monotone" dataKey="dropoff" stroke={pieColors[1]} fill={pieColors[1]} fillOpacity={0.1} strokeWidth={2} />
                          <Legend wrapperStyle={{ paddingTop: "20px" }} formatter={(value) => <span className="text-sm font-medium text-foreground">{value === "avgTime" ? "Avg Time (s)" : "Drop-off %"}</span>} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {activeTab === "ai-finder" && (
                  <div className="space-y-6">
                    <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 dark:from-purple-500/20 dark:to-blue-500/20 rounded-2xl border border-purple-500/20 dark:border-purple-500/30 p-6 shadow-sm">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-xl bg-purple-500 text-white flex items-center justify-center shadow-lg shadow-purple-500/30">
                          <Bot className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-foreground">AI Candidate Matcher</h3>
                          <p className="text-xs text-muted-foreground">Describe the ideal persona you are looking for</p>
                        </div>
                      </div>
                      
                      <div className="relative">
                        <textarea
                          value={aiPrompt}
                          onChange={(e) => setAiPrompt(e.target.value)}
                          placeholder="e.g. Looking for a student who knows React and uses TypeScript with high completion score..."
                          rows={3}
                          className="w-full rounded-xl bg-card border border-border/50 px-4 py-3 pb-12 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none shadow-sm"
                          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleAISearch(); } }}
                        />
                        <button
                          onClick={handleAISearch}
                          disabled={!aiPrompt.trim() || aiLoading}
                          className="absolute bottom-3 right-3 h-8 px-4 rounded-lg bg-purple-500 text-white text-xs font-bold hover:bg-purple-600 disabled:opacity-50 transition-all flex items-center gap-1.5 shadow-md"
                        >
                          {aiLoading ? (
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          ) : (
                            <><Sparkles className="w-3.5 h-3.5" /> Search</>
                          )}
                        </button>
                      </div>

                      <div className="flex gap-2.5 mt-4 flex-wrap">
                        {["Experienced React Developer", "Top Scorers", "Freshman Students"].map((suggestion) => (
                          <button
                            key={suggestion}
                            onClick={() => setAiPrompt(suggestion)}
                            className="text-xs px-3 py-1.5 rounded-lg bg-card border border-border text-foreground font-medium hover:border-purple-500/50 hover:text-purple-500 transition-all shadow-sm"
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    </div>

                    {aiLoading && (
                      <div className="bg-card rounded-2xl border border-border p-12 flex flex-col items-center gap-4 shadow-sm">
                        <div className="w-16 h-16 rounded-2xl bg-purple-500/10 flex items-center justify-center animate-pulse">
                          <Bot className="w-8 h-8 text-purple-500" />
                        </div>
                        <p className="text-sm font-medium text-muted-foreground animate-pulse">Analyzing respondents using AI...</p>
                      </div>
                    )}

                    {aiResults && !aiLoading && (
                      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                        <div className="bg-purple-500/5 border border-purple-500/20 rounded-2xl p-5 flex gap-4">
                          <Sparkles className="w-6 h-6 text-purple-500 flex-shrink-0" />
                          <p className="text-sm font-medium text-foreground leading-relaxed">{aiResults.reasoning}</p>
                        </div>

                        <div className="grid gap-3">
                          {aiResults.candidates.map((candidate, idx) => (
                            <div key={candidate.name} className="bg-card rounded-2xl border border-border p-5 flex items-center gap-5 shadow-sm hover:shadow-md transition-shadow">
                              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center font-display font-bold text-lg text-muted-foreground">
                                #{idx + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-base font-bold text-foreground flex items-center gap-2">
                                  {candidate.name}
                                  {idx === 0 && <Star className="w-4 h-4 text-amber-500 fill-amber-500" />}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1 font-medium">{candidate.uni} • {candidate.experience}</p>
                                {candidate.skills.length > 0 && (
                                  <div className="flex gap-1.5 mt-2 flex-wrap">
                                    {candidate.skills.map(skill => (
                                      <span key={skill} className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-md bg-primary/10 text-primary">{skill}</span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="text-xs font-semibold text-muted-foreground mb-1 uppercase tracking-wider">Match Score</p>
                                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-emerald-500/10 text-emerald-500 font-bold text-lg">
                                  {candidate.aiScore}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </div>
                )}
                
                {activeTab === "timeline" && (
                  <div className="space-y-5">
                    <div className="bg-card rounded-2xl border border-border p-5 shadow-sm">
                      <h3 className="text-base font-bold text-foreground mb-1 flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-blue-500" /> Responses & Views Over Time
                      </h3>
                      <p className="text-sm text-muted-foreground mb-6">
                        {selectedForm.createdAt} → {selectedForm.closedAt || "Present"}
                        {" · "}{selectedForm?.timeline?.length || 0} data points
                      </p>
                      {selectedForm?.timeline?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={selectedForm.timeline}>
                            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                            <XAxis dataKey="date" tick={{ fontSize: 12, fill: tickFill }} axisLine={false} tickLine={false} dy={10} />
                            <YAxis tick={{ fontSize: 12, fill: tickFill }} axisLine={false} tickLine={false} dx={-10} />
                            <Tooltip contentStyle={tooltipStyle} />
                            <Line type="monotone" dataKey="responses" stroke={barColor} strokeWidth={3} dot={{ r: 4, fill: barColor, strokeWidth: 2, stroke: isDark ? '#1e293b' : '#fff' }} activeDot={{ r: 6 }} name="Responses" />
                            <Line type="monotone" dataKey="views" stroke={lineColor2} strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3, fill: lineColor2 }} name="Views" />
                            <Legend wrapperStyle={{ paddingTop: "20px" }} formatter={(v) => <span className="text-sm font-medium text-foreground">{v}</span>} />
                          </LineChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-[300px] flex items-center justify-center text-sm text-muted-foreground">
                          No timeline data available for this form yet.
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {(() => {
                        const tl = selectedForm?.timeline || [];
                        if (tl.length === 0) return null;
                        const peakDay = tl.reduce((a, b) => b.responses > a.responses ? b : a, tl[0]);
                        const totalViews = tl.reduce((s, d) => s + d.views, 0);
                        const avgDaily = Math.round(tl.reduce((s, d) => s + d.responses, 0) / tl.length);
                        return [
                          { label: "Peak Day", value: peakDay.date, sub: `${peakDay.responses} responses` },
                          { label: "Total Views", value: totalViews.toString(), sub: "across all days" },
                          { label: "Avg. Daily", value: avgDaily.toString(), sub: "responses/day" },
                          { label: "Duration", value: `${tl.length} days`, sub: `${selectedForm.createdAt} onward` },
                        ].map((s) => (
                          <div key={s.label} className="bg-card rounded-2xl border border-border p-4 shadow-sm hover:shadow-md transition-shadow">
                            <span className="text-xs font-semibold text-muted-foreground tracking-wider uppercase">{s.label}</span>
                            <p className="text-2xl font-display font-bold text-foreground mt-2">{s.value}</p>
                            <p className="text-xs text-muted-foreground mt-1 font-medium">{s.sub}</p>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                )}

                {activeTab === "respondents" && (
                  <div className="bg-card rounded-2xl border border-border overflow-hidden shadow-sm">
                    {selectedForm?.respondents?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-border bg-muted/30">
                              <th className="text-left px-6 py-4 text-xs font-bold text-muted-foreground uppercase tracking-widest">Name</th>
                              <th className="text-left px-6 py-4 text-xs font-bold text-muted-foreground uppercase tracking-widest">Status</th>
                              <th className="text-left px-6 py-4 text-xs font-bold text-muted-foreground uppercase tracking-widest">Time Spent</th>
                              <th className="text-left px-6 py-4 text-xs font-bold text-muted-foreground uppercase tracking-widest">Stopped At</th>
                              {selectedForm.respondents.some(r => r.score > 0) && (
                                <th className="text-left px-6 py-4 text-xs font-bold text-muted-foreground uppercase tracking-widest">Score</th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {selectedForm.respondents.map((r, i) => (
                              <tr key={i} className="border-b border-border/40 hover:bg-muted/30 transition-colors">
                                <td className="px-6 py-4">
                                  <p className="text-sm font-bold text-foreground">{r.name}</p>
                                  {(r.uni || r.experience) && <p className="text-xs font-medium text-muted-foreground mt-0.5">{r.uni} {r.uni && r.experience ? '·' : ''} {r.experience}</p>}
                                </td>
                                <td className="px-6 py-4">
                                  {r.completed ? (
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-500/10 text-xs font-bold text-emerald-500 border border-emerald-500/20">
                                      <CheckCircle2 className="w-3.5 h-3.5" /> Completed
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-red-500/10 text-xs font-bold text-red-500 border border-red-500/20">
                                      <XCircle className="w-3.5 h-3.5" /> Dropped
                                    </span>
                                  )}
                                </td>
                                <td className="px-6 py-4 text-sm font-medium text-muted-foreground">{r.timeSpent}</td>
                                <td className="px-6 py-4 text-sm text-muted-foreground">
                                  {r.stoppedAt ? (
                                    <span className="flex items-center gap-1.5 font-medium text-amber-500">
                                      <AlertTriangle className="w-3.5 h-3.5" /> {r.stoppedAt}
                                    </span>
                                  ) : "—"}
                                </td>
                                {selectedForm.respondents.some(res => res.score > 0) && (
                                  <td className="px-6 py-4">
                                    {r.score > 0 ? (
                                      <span className={`text-sm font-black ${
                                        r.score >= 85 ? "text-emerald-500" : r.score >= 70 ? "text-amber-500" : "text-red-500"
                                      }`}>{r.score}</span>
                                    ) : "—"}
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="py-20 flex flex-col items-center justify-center text-center">
                        <Users className="w-12 h-12 text-muted-foreground mb-4 opacity-50" />
                        <h3 className="text-lg font-bold text-foreground">No Respondents Yet</h3>
                        <p className="text-sm text-muted-foreground mt-1">When users respond to your form, they will appear here.</p>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === "questions" && (
                  <div className="space-y-4">
                    {selectedForm?.questions?.length > 0 ? (
                      selectedForm.questions.map((q, i) => (
                        <div key={q.id || i} className="bg-card rounded-2xl border border-border p-5 flex items-center justify-between shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex items-center gap-4 flex-1 min-w-0">
                            <span className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center text-sm font-black text-blue-500 flex-shrink-0">
                              {i + 1}
                            </span>
                            <div className="min-w-0">
                              <p className="text-base font-bold text-foreground truncate">{q.text}</p>
                              <p className="text-xs font-semibold text-muted-foreground tracking-wider uppercase mt-1">{q.type?.replace("_", " ")}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-6 text-sm font-medium text-muted-foreground flex-shrink-0">
                            <span className="flex items-center gap-1.5 bg-muted/50 px-3 py-1.5 rounded-lg">
                              <Clock className="w-4 h-4 text-emerald-500" /> {q.avgTime}s avg
                            </span>
                            <span className="flex items-center gap-1.5 bg-muted/50 px-3 py-1.5 rounded-lg">
                              <AlertTriangle className="w-4 h-4 text-amber-500" /> {q.dropoff}% drop
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="bg-card rounded-2xl border border-border p-20 flex flex-col items-center justify-center text-center shadow-sm">
                        <FileText className="w-12 h-12 text-muted-foreground mb-4 opacity-50" />
                        <h3 className="text-lg font-bold text-foreground">No Questions Found</h3>
                        <p className="text-sm text-muted-foreground mt-1">There are no questions attached to this form.</p>
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
            </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Forms;
